import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { tr } from '../i18n';
import api from '../api/axios';
import { Catalog, CatalogEntry, EMPTY_CATALOG } from './types';

export const CATALOG_UPDATED_EVENT = 'catalog:updated';

// Shape of raw API response items
type ApiEntry = {
  name: string;
  displayName: string | null;
  imageUrl: string | null;
};

type ApiCatalogResponse = {
  heroes: ApiEntry[];
  abilities: ApiEntry[];
  items: ApiEntry[];
};

/**
 * Backend stores uploaded image URLs as Spring-relative paths like `/files/catalog/<uuid>.png`
 * (no `/api` prefix — Spring context-path adds it). Browser requests need the real HTTP path
 * `/api/files/catalog/<uuid>.png`, so we normalize here once and consumers can use the value
 * verbatim. External URLs (`http(s)://...`) and overrides already prefixed with `/api` are kept as-is.
 */
function normalizeImageUrl(raw: string | null): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('/files/')) return `/api${trimmed}`;
  return trimmed;
}

function toRecord(entries: ApiEntry[]): Record<string, CatalogEntry> {
  const result: Record<string, CatalogEntry> = {};
  for (const entry of entries) {
    result[entry.name] = {
      name: entry.name,
      displayName: entry.displayName,
      imageUrl: normalizeImageUrl(entry.imageUrl),
    };
  }
  return result;
}

type CatalogContextShape = {
  catalog: Catalog;
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

const CatalogContext = createContext<CatalogContextShape>({
  catalog: EMPTY_CATALOG,
  loading: false,
  error: null,
  refetch: () => undefined,
});

export const CatalogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [catalog, setCatalog] = useState<Catalog>(EMPTY_CATALOG);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track whether the initial fetch has been triggered to avoid duplicate mounts
  const fetchedRef = useRef(false);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiCatalogResponse>('/api/catalog');
      const data = response.data;
      setCatalog({
        heroes: toRecord(data.heroes ?? []),
        abilities: toRecord(data.abilities ?? []),
        items: toRecord(data.items ?? []),
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : tr.shell.catalog.loadFailed;
      // Keep catalog as-is (empty or previous value); consumers fall back to Steam CDN.
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount — non-blocking: empty catalog is returned immediately,
  // consumers work with fallback (Steam CDN + toDotaDisplayName) until data arrives.
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchCatalog();
  }, [fetchCatalog]);

  // Admin page dispatches this event after saving overrides so the catalog stays fresh.
  useEffect(() => {
    const handler = () => fetchCatalog();
    window.addEventListener(CATALOG_UPDATED_EVENT, handler);
    return () => window.removeEventListener(CATALOG_UPDATED_EVENT, handler);
  }, [fetchCatalog]);

  return (
    <CatalogContext.Provider value={{ catalog, loading, error, refetch: fetchCatalog }}>
      {children}
    </CatalogContext.Provider>
  );
};

export { CatalogContext };
